/**
 * Pricing page. Purchasable plans + checkout are rendered by Clerk's
 * <PricingTable> (source of truth, brand-themed via clerkAppearance). The
 * not-yet-purchasable Developer tier is a waitlist strip (ComingSoonBanner),
 * followed by the guarantee note and a feature-comparison table.
 */

import { useMemo } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";

// Material UI
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Box,
  Button,
  Container,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { styled, useTheme} from "@mui/material/styles";

// Material UI Icons
import Business from "@mui/icons-material/Business";
import Check from "@mui/icons-material/Check";
import Close from "@mui/icons-material/Close";
import ArrowForward from "@mui/icons-material/ArrowForward";
import CheckCircleOutline from "@mui/icons-material/CheckCircleOutline";
import ExpandMore from "@mui/icons-material/ExpandMore";

// Clerk (source of truth for purchasable plans + checkout)
import { PricingTable } from "@clerk/clerk-react";

// Core components
import ComingSoonBanner from './ComingSoonBanner';
import PricingHeader from './PricingHeader';
import clerkAppearance from 'assets/theme/clerkAppearance.js';
import useSeo from 'utils/useSeo';
import { pageDescription } from "utils/routeMeta";
import { buildPlanComparisonRows } from './planComparisonRows';
import { buildPricingSubtitle } from './pricingSubtitle';
import { useEnsurePlans } from 'store/functions/entitlements';
import { selectPlanCatalogItems } from 'store/reducers/uiReducer';
import { buildPricingFaqs } from './pricingFaqs';
import { track, EVENTS } from "utils/analytics.js";

/*
Short reassurance line above the plan cards. "No credit card" is scoped to the
free plan on purpose: the Professional trial needs a card, and a bare "No credit
card required" sitting next to a trial button reads as covering the trial. The
trial itself is left out here because the guarantee note and the FAQ already
cover it, and a third mention on one page starts to read like fine print.
*/
export const PRICING_REASSURANCES = [
  "No credit card for the free plan",
  "Routes in seconds",
  "Spreadsheet upload (Excel / CSV)",
];

/*
The line under the page title, built from the plan catalog rather than written.

It shipped as "Every optimizer and every constraint is on the free plan", which
the catalog flatly contradicts: fixed_assignments, resupply, multi_day,
heterogeneous_travel_costs and road_network_costs are all false on the free
tier, and the comparison table a few inches below rendered crosses for them on
the same screen.

Correcting it once was not enough, and that is the point. The corrected version
— "Professional adds bigger fleets, multi-day routes, resupply and real road
distances" — named four of the five, and would have gone on naming those four
whether a sixth capability appeared or one of them was granted to free. A
sentence cannot notice the catalog moving underneath it.

So the list is now computed from the difference between the two plans. See
buildPricingSubtitle for what it can and cannot check; while /plans is in
flight it returns a line that makes no checkable claim at all.
*/

const StyledContainer = styled(Container)(() => ({
    paddingTop: "0rem",
    paddingBottom: "3rem",
}));

const StyledPricingContainer = styled(Container)(({ theme }) => ({
    paddingLeft: "1rem",
    paddingRight: "1rem",
    [theme.breakpoints.up("md")]: {
      paddingLeft: "2rem",
      paddingRight: "2rem",
    },
}));

const StyledGuaranteeBox = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    marginTop: "1.5rem",
    padding: "1rem",
    borderRadius: "1rem",
  backgroundColor: theme.palette.grey?.[200] || "rgba(0,0,0,0.03)",
  border: `1px solid ${theme.palette.grey?.[400] || "rgba(0,0,0,0.08)"}`,
    [theme.breakpoints.up("lg")]: {
      justifyContent: "center",
    },
}));

const StyledAvatar = styled(Avatar)(() => ({
    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
}));

const StyledTable = styled(Table)(() => ({
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: 0,
}));

const StyledTableCellHead = styled(TableCell)(({ theme }) => ({
    fontWeight: 800,
  color: theme.palette.text.primary,
  borderBottom: `1px solid ${theme.palette.grey?.[400] || "rgba(0,0,0,0.08)"}`,
    paddingTop: "1rem",
    paddingBottom: "1rem",
}));

const StyledTableRow = styled(TableRow)(() => ({
    "&:last-child td": {
      borderBottom: "none",
    },
}));

const StyledTableCellBody = styled(TableCell)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.grey?.[400] || "rgba(0,0,0,0.08)"}`,
    paddingTop: "0.875rem",
    paddingBottom: "0.875rem",
  color: theme.palette.text.primary,
}));

/**
 * Render one comparison cell. `null` means the catalog couldn't answer, which
 * shows as an em dash with a screen-reader label — deliberately NOT a cross,
 * since "we don't know" and "your plan excludes this" are different claims and
 * only one of them is safe to make to someone about to enter card details.
 */
function renderCell(value, theme) {
  if (value === true) {
    return <Check role="img" aria-label="Included" sx={{ width: "1.5rem", height: "1.5rem", color: theme.palette.success.main }} />;
  }
  if (value === false) {
    return <Close role="img" aria-label="Not included" sx={{ width: "1.25rem", height: "1.25rem", color: theme.palette.grey[400] }} />;
  }
  if (value === null || value === undefined) {
    return <Box component="span" aria-label="Not available">—</Box>;
  }
  return value;
}

function Pricing() {
  useSeo({
    title: "Pricing",
    description: pageDescription('/pricing'),
    canonicalPath: "/pricing",
  });
  const theme = useTheme();
  // Derived from GET /plans, not restated here. The previous hardcoded array
  // drifted from what customers were actually sold — it claimed 5/25 saved
  // models and a 50/500 monthly-solve cap while the Clerk cards on this same
  // page said 10/unlimited and "unlimited solves". See planComparisonRows.js.
  useEnsurePlans();
  const planCatalog = useSelector(selectPlanCatalogItems);
  const tableRows = useMemo(() => buildPlanComparisonRows(planCatalog), [planCatalog]);
  const faqs = useMemo(() => buildPricingFaqs(planCatalog), [planCatalog]);
  // Same catalog as the table and the FAQ, so the three cannot disagree.
  const headerDescription = useMemo(() => buildPricingSubtitle(planCatalog), [planCatalog]);
  return (
    <>
      {/* Was: badge "Flexible Pricing", title "Choose the best plan for your
          needs", description "Professional routing solutions for fleets of all
          sizes. Choose the plan that fits your business goals." — three lines
          that said nothing, and "choose" twice. Now it states the actual offer. */}
      <PricingHeader
        light
        badge="Pricing"
        title="Start free. Upgrade when you outgrow it."
        description={headerDescription}
      />
      <StyledContainer id='pricing-container1'>
        <StyledPricingContainer
          id='pricing-container2'
          maxWidth="xl"
          sx={{
            paddingBottom: "0.5rem",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "center", mt: 1, mb: 2 }}>
            <Stack
              component="ul"
              direction={{ xs: "column", sm: "row" }}
              spacing={{ xs: 1, sm: 3 }}
              alignItems="center"
              justifyContent="center"
              sx={{ listStyle: "none", m: 0, p: 0 }}
            >
              {PRICING_REASSURANCES.map((perk) => (
                <Stack component="li" key={perk} direction="row" spacing={0.75} alignItems="center">
                  <CheckCircleOutline aria-hidden="true" sx={{ fontSize: 16, color: theme.palette.primary.main }} />
                  <Typography variant="body2" sx={{ fontSize: "0.85rem", color: theme.palette.text.secondary, fontWeight: 500 }}>
                    {perk}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
            <Box sx={{ width: "100%", maxWidth: "960px" }}>
              <PricingTable appearance={clerkAppearance} />
              <ComingSoonBanner />
            </Box>
          </Box>
          <StyledGuaranteeBox>
            <Box marginRight="1rem">
              <StyledAvatar sx={{backgroundColor: theme.palette.common.white, width: '2.5rem', height: '2.5rem'}}>
                <Business
                  sx={{
                    color: theme.palette.primary.main,
                    width: "1.25rem",
                    height: "1.25rem",
                  }}
                />
              </StyledAvatar>
            </Box>
            <Grid item xs={12}>
              <Box
                component="p"
                sx={{
                  fontWeight: 500,
                  lineHeight: 1.4,
                  margin: 0,
                  fontSize: "0.875rem",
                  color: theme.palette.text.primary,
                }}
              >
                {/* Matches the Clerk card's CTA. This previously promised a
                    "7-day money back guarantee" while the card beside it offered
                    a 30-day free trial — two different escape hatches in one
                    view, which reads as fine print rather than reassurance. */}
                <strong style={{color: theme.palette.primary.extraDark, fontWeight: 800}}>Cancel any time during your 30-day trial</strong> and you won't be charged. No questions asked.
              </Box>
            </Grid>
          </StyledGuaranteeBox>
          <Grid container component={Box} justifyContent="center" marginTop={theme.breakpoints.down('md') ? "3rem" : "1.5rem"}>
            <Grid item xs={12} lg={10}>
              <Box textAlign="center" marginBottom="0.5rem">
                <Typography variant="h4" component="h2" sx={{fontWeight: 800, color: theme.palette.text.primary}}>
                  Compare plans
                </Typography>
              </Box>
              <TableContainer
                component={Box}
                sx={{
                  boxShadow: "0 4px 6px rgba(0,0,0,0.02)",
                  borderRadius: "1rem",
                  border: `1px solid ${theme.palette.grey[400]}`,
                  backgroundColor: theme.palette.grey[200],
                }}
              >
                <StyledTable aria-label="simple table">
                  <TableHead>
                    <TableRow>
                      <StyledTableCellHead align="left">
                        Features
                      </StyledTableCellHead>
                      <StyledTableCellHead align="center">
                        Starter
                      </StyledTableCellHead>
                      <StyledTableCellHead align="center">
                        Professional
                      </StyledTableCellHead>
                      <StyledTableCellHead align="center">
                        Developer
                      </StyledTableCellHead>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tableRows.map((row) => (
                      <StyledTableRow key={row.label}>
                        <StyledTableCellBody align="left" sx={{fontWeight: 600}}>
                          {row.label}
                        </StyledTableCellBody>
                        {["starter", "professional", "developer"].map((tier) => (
                          <StyledTableCellBody align="center" key={tier}>
                            {renderCell(row[tier], theme)}
                          </StyledTableCellBody>
                        ))}
                      </StyledTableRow>
                    ))}
                  </TableBody>
                </StyledTable>
              </TableContainer>
            </Grid>
          </Grid>

          {/* For the minority on this page who are developers rather than operators.
              The heading addresses them explicitly so that everyone else can skip it in
              one glance -- this page is written for schedulers and owners, and a line
              that reads as relevant to all of them would just be in the way.

              It sits after the plan comparison and before the FAQ because this is the
              point where somebody who was weighing "or we build it ourselves" has
              finished reading what the application costs. */}
          <Grid container component={Box} justifyContent="center" sx={{ mt: { xs: "3rem", md: "4rem" } }}>
            <Grid item xs={12} lg={10}>
              <Box
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  p: { xs: 2.5, md: 3 },
                  backgroundColor: "background.paper",
                }}
              >
                <Typography variant="h6" component="h2" sx={{ fontWeight: 800, color: theme.palette.text.primary, mb: 1 }}>
                  Are you a developer?
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.7, maxWidth: "60ch", mb: 1.5 }}>
                  If you want the travel-time data behind this and not the application, that is a
                  separate product. WayMatrix is our distance-matrix and directions API — you send
                  coordinates, it returns drive times. Openrouteservice-compatible, priced per
                  element, free to try.
                </Typography>
                <Typography
                  component="a"
                  variant="body2"
                  href="https://www.waymatrix.io"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ fontWeight: 700, color: "primary.main", textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
                >
                  waymatrix.io
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {/* FAQ */}
          <Grid container component={Box} justifyContent="center" sx={{ mt: { xs: "3rem", md: "4rem" } }}>
            <Grid item xs={12} lg={10}>
              <Box textAlign="center" marginBottom="2rem">
                <Typography variant="h4" component="h2" sx={{ fontWeight: 800, color: theme.palette.text.primary }}>
                  Frequently asked questions
                </Typography>
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {faqs.map((faq, index) => (
                  <Accordion
                    key={faq.q}
                    disableGutters
                    elevation={0}
                    sx={{
                      borderRadius: "0.75rem !important",
                      border: `1px solid ${theme.palette.grey[400] || "rgba(0,0,0,0.12)"}`,
                      backgroundColor: theme.palette.grey[200] || "rgba(0,0,0,0.02)",
                      "&:before": { display: "none" },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMore sx={{ color: theme.palette.text.secondary }} />}
                      id={`pricing-faq-header-${index}`}
                      aria-controls={`pricing-faq-content-${index}`}
                    >
                      <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: theme.palette.text.primary }}>
                        {faq.q}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0, pb: 2 }}>
                      <Typography sx={{ color: theme.palette.text.secondary, fontSize: "0.9375rem", lineHeight: 1.6 }}>
                        {faq.a}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            </Grid>
          </Grid>

          {/* Bottom CTA */}
          <Grid container component={Box} justifyContent="center" marginTop="3.5rem">
            <Grid item xs={12} lg={10}>
              <Box
                sx={{
                  p: { xs: 3, md: 5 },
                  textAlign: "center",
                  borderRadius: "1rem",
                  backgroundColor: theme.palette.primary.extraLight,
                  border: `1px solid ${theme.palette.primary.light || "rgba(0,146,139,0.2)"}`,
                }}
              >
                <Typography variant="h4" component="h2" sx={{ fontWeight: 800, color: theme.palette.dark.main, mb: 1.5 }}>
                  Try it with your own data
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: theme.palette.text.secondary, maxWidth: "560px", mx: "auto", mb: 3, fontSize: "1rem", lineHeight: 1.5 }}
                >
                  Upload your stops and get routes back in seconds, or open one of the worked examples and change it. No account needed to try.
                </Typography>
                <Box sx={{ display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
                  <Button
                    component={Link}
                    to="/model-builder"
                    variant="contained"
                    endIcon={<ArrowForward />}
                    onClick={() => track(EVENTS.CTA_CLICKED, { cta_id: "pricing_final_builder", page: "pricing" })}
                    sx={{
                      backgroundColor: theme.palette.primary.main,
                      px: 3.5,
                      py: 1.25,
                      fontWeight: 700,
                      textTransform: "none",
                      borderRadius: "8px",
                      "&:hover": { backgroundColor: theme.palette.primary.extraDark },
                    }}
                  >
                    Plan with your own fleet
                  </Button>
                  <Button
                    component={Link}
                    to="/use-cases"
                    variant="outlined"
                    onClick={() => track(EVENTS.CTA_CLICKED, { cta_id: "pricing_final_examples", page: "pricing" })}
                    sx={{
                      px: 3.5,
                      py: 1.25,
                      fontWeight: 700,
                      textTransform: "none",
                      borderRadius: "8px",
                      borderColor: theme.palette.primary.main,
                      color: theme.palette.primary.main,
                      "&:hover": { borderColor: theme.palette.primary.extraDark, backgroundColor: theme.palette.primary.extraLight },
                    }}
                  >
                    Browse worked examples
                  </Button>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </StyledPricingContainer>
      </StyledContainer>
    </>
  );
}

export default Pricing;
